/**
 * FlyerDeals — response shape for `GET /api/flyerdeals` (T12.0.15 Step 5).
 *
 * The backend resolver projects a FlyerDeal row into a snake_case shape
 * that mirrors `T12.0.15-flyer-deals.md § Details § API`. The endpoint
 * wraps the per-store resolver output with `storelocation_id`, `store_name`,
 * and `is_favorite`.
 *
 * Casing: snake_case end-to-end. The resolver returns snake_case, the
 * endpoint preserves it, and the client renders the same fields verbatim.
 * No transform — match precedent set by `/api/retaileroffers/*`.
 *
 * Numeric pricing fields go through `pgNumeric` (Postgres NUMERIC ships as
 * a string; coerce to number at the schema boundary).
 */

import { z } from 'zod';
import { PRICE_BASIS_UNITS } from './packing';
import { pgNumeric, priceCurrencySchema } from './primitives';

export const flyerDealsRowSchema = z.object({
  id: z.string(),
  source: z.string(),
  source_url: z.string(),
  source_item_id: z.string(),
  matched_retaileroffer_id: z.string().nullable(),
  title: z.string(),
  brand: z.string().optional(),
  image_url: z.string().optional(),
  description: z.string().optional(),
  regular_price: pgNumeric.optional(),
  sale_price: pgNumeric,
  price_currency: priceCurrencySchema,
  sale_description: z.string().optional(),
  // T64.1 D8 — what `sale_price` is charged for. Undefined when the source
  // supplied none (every Flipp deal before T64.1 D9) — the client renders
  // the price with no invented unit, exactly as it does today.
  // T64.1 D10 — canonical unit spelling (CANONICAL_UNITS in ./packing.ts),
  // replacing the retired `per_pound`/`per_kilogram`/`per_item` spelling.
  price_basis: z.enum(PRICE_BASIS_UNITS).optional(),
  valid_from: z.string(),
  valid_to: z.string(),
  // The resolver emits Date objects which Express JSON-serializes as ISO
  // strings on the wire.
  created_at: z.string(),
  updated_at: z.string(),
  is_expired: z.boolean(),
  // T12.1.8.7 — the product line(s) this deal covers, from the
  // `flyerdeals_systemproducts` link table. Drives the add: a deal with ≥1 line
  // is addable (SystemProduct-first — no matched store offer required); a deal
  // with several lines is a "combo" the client prompts the household to pick
  // from. Empty when the ingestion resolver could not link the deal to any
  // line (rare); such a deal stays non-addable. `.optional()` so legacy cached
  // rows still parse.
  lines: z
    .array(
      z.object({
        system_product_id: z.string(),
        name: z.string(),
      }),
    )
    .optional(),
  category_tags: z.array(z.object({
    id: z.string(), name: z.string(), parentId: z.string().nullable(), weight: z.number(),
  })).optional(),
  attribute_tags: z.array(z.object({
    id: z.string(), name: z.string(), weight: z.number(),
  })).optional(),
});
export type FlyerDealsRow = z.infer<typeof flyerDealsRowSchema>;
export type FlyerDealLine = NonNullable<FlyerDealsRow['lines']>[number];

export const flyerDealsStoreGroupSchema = z.object({
  storelocation_id: z.string(),
  store_name: z.string(),
  is_favorite: z.boolean(),
  deals: z.array(flyerDealsRowSchema),
});
export type FlyerDealsStoreGroup = z.infer<typeof flyerDealsStoreGroupSchema>;

export const dealsSearchFilterOptionSchema = z.object({
  term: z.string(),
  count: z.number().int().nonnegative(),
  defaultIntent: z.union([z.literal('require'), z.literal('exclude')]),
  group: z.union([z.literal('category'), z.literal('attribute')]),
});
export type DealsSearchFilterOption = z.infer<typeof dealsSearchFilterOptionSchema>;

export const flyerDealsResponseSchema = z.object({
  stores: z.array(flyerDealsStoreGroupSchema),
  filter_options: z.object({
    categories: z.array(dealsSearchFilterOptionSchema),
    attributes: z.array(dealsSearchFilterOptionSchema),
  }).optional(),
});
export type FlyerDealsResponse = z.infer<typeof flyerDealsResponseSchema>;

/**
 * T55.3 — fit-ranked deals search response (`GET /api/flyerdeals/search`).
 * Each row is `flyerDealsRowSchema` plus a `fit` score (mean per-term
 * coverage, Details' "reuse the prototype's mean coverage formula") and a
 * `matched_terms` breakdown so the surface can render "matched tags" +
 * ranking without a second request. `parsed`/`include_rewrites`/
 * `exclude_rewrites` feed the correction line + undo (Deliverable 1);
 * `filter_options` are the T55.8 drawer inputs; applying them remains local.
 */
export const dealsSearchRowSchema = flyerDealsRowSchema.extend({
  fit: z.number(),
  matched_terms: z.record(z.string(), z.number()),
  category_tags: z.array(z.object({
    id: z.string(), name: z.string(), parentId: z.string().nullable(), weight: z.number(),
  })),
  attribute_tags: z.array(z.object({
    id: z.string(), name: z.string(), weight: z.number(),
  })),
  store_location_ids: z.array(z.string()),
});
export type DealsSearchRow = z.infer<typeof dealsSearchRowSchema>;

export const dealsSearchParsedSchema = z.object({
  include: z.array(z.string()),
  exclude: z.array(z.string()),
});

export const dealsSearchRewriteSchema = z.object({
  term: z.string(),
  original: z.string(),
  corrected: z.boolean(),
});

export const dealsSearchResponseSchema = z.object({
  deals: z.array(dealsSearchRowSchema),
  parsed: dealsSearchParsedSchema,
  include_rewrites: z.array(dealsSearchRewriteSchema),
  exclude_rewrites: z.array(dealsSearchRewriteSchema),
  filter_options: z.object({
    categories: z.array(dealsSearchFilterOptionSchema),
    attributes: z.array(dealsSearchFilterOptionSchema),
  }),
  truncated: z.boolean(),
});
export type DealsSearchResponse = z.infer<typeof dealsSearchResponseSchema>;

export const dealsSearchSuggestionSchema = z.object({
  query: z.string(),
  source: z.union([
    z.literal('list'), z.literal('tracked'), z.literal('preference'),
    z.literal('history'), z.literal('aggregate'), z.literal('catalog'),
  ]),
});
export type DealsSearchSuggestion = z.infer<typeof dealsSearchSuggestionSchema>;
export const dealsSearchSuggestionsResponseSchema = z.object({
  suggestions: z.array(dealsSearchSuggestionSchema),
});
export type DealsSearchSuggestionsResponse = z.infer<typeof dealsSearchSuggestionsResponseSchema>;
